import { Download, ExternalLink } from 'lucide-react';
import { PdfPreview } from './PdfPreview';

interface DocumentPreviewProps {
  contentType: string;
  fileName: string;
  fileUrl: string;
}

/**
 * Inline preview for an uploaded document. Switches by MIME type:
 *  - `image/*` → native `<img>`
 *  - `application/pdf` → locally bundled PDF.js renderer
 *  - anything else → a download link
 *
 * `fileUrl` must be a presigned, time-limited URL. We reuse the same one the
 * sheet already has from `UploadedDocumentDto.current_revision.file_url`.
 */
export function DocumentPreview({ fileUrl, fileName, contentType }: DocumentPreviewProps) {
  const type = contentType.toLowerCase();

  if (type.startsWith('image/')) {
    return (
      <img src={fileUrl} alt={fileName} className="max-h-[70vh] w-full rounded-md border bg-muted/30 object-contain" />
    );
  }

  if (type === 'application/pdf') {
    return (
      <div>
        <div className="mb-1 flex justify-end">
          <a
            href={fileUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            <ExternalLink className="h-3.5 w-3.5" />
            Open in new tab
          </a>
        </div>
        <PdfPreview key={fileUrl} fileUrl={fileUrl} fileName={fileName} />
      </div>
    );
  }

  return (
    <a
      href={fileUrl}
      target="_blank"
      rel="noreferrer"
      className="flex items-center justify-center gap-2 rounded-md border border-dashed p-6 text-sm text-muted-foreground hover:bg-accent">
      <Download className="h-4 w-4" />
      Preview not available — download {fileName}
    </a>
  );
}
